import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAdjustmentTypes } from '@components/layout/Modals/BillingSettingsModal/hooks/useAdjustmentTypes'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@store/index'
import { getAllAdjustmentTypeSettings } from '@store/actions/adjustmentType.action'
import { SortDirection } from '@constants/Common/sort-direction.constant'
import { SettingStatus } from '@dataTypes/settingStatus.type'

const ADJUSTMENTS_LOADING_CONTAINER = '.settings-accounting-adjustments'
type AdjustmentFilterMap = Record<string, string[]>

export const useAdjustments = (
  searchQuery: string,
  filters: AdjustmentFilterMap,
  ...args: Parameters<typeof useAdjustmentTypes>
): ReturnType<typeof useAdjustmentTypes> & {
  adjustmentTypes: ReturnType<typeof useAdjustmentTypes>['adjustmentTypes']
  adjustmentCount: number
  currentPage: number
  pageSize: number
  listLoading: boolean
  handlePageChange: (page: number) => void
  handlePageSizeChange: (nextPageSize: number) => void
  handleTableSortChange: (sort: {
    key: string
    direction: SortDirection
  }) => void
} => {
  const [showToast, showLoading, hideLoading] = args
  const dispatch = useDispatch<AppDispatch>()
  const selectedClinic = useSelector(
    (state: RootState) => state.clinic.selectedClinic,
  )
  const { count } = useSelector((state: RootState) => state.adjustmentType)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sort, setSort] = useState<string>('name:ASC')
  const [listLoading, setListLoading] = useState(true)
  const initialLoadRef = useRef(true)

  const showPageLoading = useCallback<
    NonNullable<Parameters<typeof useAdjustmentTypes>[1]>
  >(
    (options) => {
      showLoading?.({ ...options, container: ADJUSTMENTS_LOADING_CONTAINER })
    },
    [showLoading],
  )

  const hidePageLoading = useCallback<
    NonNullable<Parameters<typeof useAdjustmentTypes>[2]>
  >(() => {
    hideLoading?.(ADJUSTMENTS_LOADING_CONTAINER)
  }, [hideLoading])

  const statusFilters = useMemo(
    () =>
      filters.status?.filter(
        (value): value is SettingStatus =>
          value === SettingStatus.Enabled || value === SettingStatus.Disabled,
      ),
    [filters.status],
  )
  const modifierFilters = useMemo(
    () =>
      filters.modifier?.filter(
        (value): value is 'Positive' | 'Negative' =>
          value === 'Positive' || value === 'Negative',
      ),
    [filters.modifier],
  )
  const categoryFilters = useMemo(
    () =>
      filters.category?.filter(
        (value): value is 'Adjustment' | 'Collection' =>
          value === 'Adjustment' || value === 'Collection',
      ),
    [filters.category],
  )

  const buildListQuery = useCallback(
    (page: number) => {
      const q = searchQuery.trim()
      return {
        ...(q ? { search: q } : {}),
        ...(statusFilters?.length
          ? {
              status:
                statusFilters.length === 1 ? statusFilters[0] : statusFilters,
            }
          : {}),
        ...(categoryFilters?.length
          ? {
              category:
                categoryFilters.length === 1
                  ? categoryFilters[0]
                  : categoryFilters,
            }
          : {}),
        ...(modifierFilters?.length
          ? {
              modifier:
                modifierFilters.length === 1
                  ? modifierFilters[0]
                  : modifierFilters,
            }
          : {}),
        sort,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      }
    },
    [
      categoryFilters,
      modifierFilters,
      pageSize,
      searchQuery,
      sort,
      statusFilters,
    ],
  )

  const getListFilters = useCallback(
    () => buildListQuery(currentPage),
    [buildListQuery, currentPage],
  )

  const modalHooks = useAdjustmentTypes(
    showToast,
    showPageLoading,
    hidePageLoading,
    getListFilters,
  )

  const { reset } = modalHooks

  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, filters.category, filters.modifier, filters.status])

  useEffect(() => {
    initialLoadRef.current = true
    setListLoading(true)
    setCurrentPage(1)
    reset()
    dispatch(getAllAdjustmentTypeSettings(buildListQuery(1)))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clinic-only; buildListQuery is latest
  }, [dispatch, reset, selectedClinic?.clinic_id])

  useEffect(() => {
    const isInitialLoad = initialLoadRef.current
    if (isInitialLoad) setListLoading(true)
    void dispatch(getAllAdjustmentTypeSettings(getListFilters())).finally(
      () => {
        if (!isInitialLoad) return
        initialLoadRef.current = false
        setListLoading(false)
      },
    )
  }, [dispatch, getListFilters])

  const handlePageChange = useCallback((page: number): void => {
    setCurrentPage(page)
  }, [])

  const handlePageSizeChange = useCallback((nextPageSize: number): void => {
    setPageSize(nextPageSize)
    setCurrentPage(1)
  }, [])

  const handleTableSortChange = useCallback(
    ({ key, direction }: { key: string; direction: SortDirection }): void => {
      if (
        key === 'status' ||
        key === 'name' ||
        key === 'modifier' ||
        key === 'category'
      ) {
        setSort(`${key}:${direction}`)
        setCurrentPage(1)
      }
    },
    [],
  )

  const displayAdjustmentTypes = modalHooks.getFilteredData('', {})

  return {
    ...modalHooks,
    adjustmentTypes: displayAdjustmentTypes,
    adjustmentCount: count,
    currentPage,
    pageSize,
    listLoading,
    handlePageChange,
    handlePageSizeChange,
    handleTableSortChange,
  }
}

export default useAdjustments
